import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Loader2, Inbox, CheckCircle2, XCircle, Plus, Search,
    Building2, MoreHorizontal, Pencil, Trash2, ChevronUp, ChevronDown,
    ChevronsUpDown, ShieldCheck, ShieldX, Eye, CalendarDays, Tag,
    MapPin, Users, TicketPercent, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OfferFormDialog from "@/components/offers/OfferFormDialog";
import { adminOffersApi } from "@/lib/api/offers";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { categoriesApi } from "@/lib/api/categories";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", "DRAFT"];

const STATUS_STYLE = {
    APPROVED: "border-success/40 text-success bg-success/5",
    PENDING:  "border-warning/40 text-warning bg-warning/5",
    REJECTED: "border-destructive/40 text-destructive bg-destructive/5",
    DRAFT:    "border-border text-muted-foreground",
};

function StatusBadge({ status, t }) {
    return (
        <Badge variant="outline" className={STATUS_STYLE[status] || STATUS_STYLE.DRAFT}>
            {t(`offers.status.${status}`)}
        </Badge>
    );
}

function SortIcon({ col, sort }) {
    if (sort.col !== col) return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sort.dir === "asc"
        ? <ChevronUp className="h-3.5 w-3.5 text-primary" />
        : <ChevronDown className="h-3.5 w-3.5 text-primary" />;
}

function DecisionDialog({ open, onOpenChange, offer, decision, onDecided, t, isRTL }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => { if (open) { setReason(""); setError(""); } }, [open]);
    if (!offer) return null;
    const isReject = decision === "REJECTED";

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true); setError("");
        try {
            const updated = await adminOffersApi.decide(offer.id, {
                decision,
                rejection_reason: isReject ? reason.trim() : null,
            });
            toast.success(t(isReject ? "offers.toasts.rejected" : "offers.toasts.approved"));
            onDecided(updated);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-2xl" dir={isRTL ? "rtl" : "ltr"}
                data-testid={isReject ? "offer-reject-dialog" : "offer-approve-dialog"}>
                <DialogHeader>
                    <DialogTitle>{t(isReject ? "offers.rejectTitle" : "offers.approveTitle")}</DialogTitle>
                    <DialogDescription>{offer.title_en} · {offer.provider_name}</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    {isReject ? (
                        <div>
                            <Label htmlFor="reject_reason">{t("offers.fields.rejectionReason")}</Label>
                            <Textarea id="reject_reason" rows={4} required
                                placeholder={t("offers.fields.rejectionReasonPlaceholder")}
                                value={reason} onChange={(e) => setReason(e.target.value)}
                                className="mt-1.5" data-testid="offer-rejection-reason" />
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">{t("offers.approveBody")}</p>
                    )}
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
                        <Button type="submit" disabled={submitting || (isReject && !reason.trim())}
                            className={isReject ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                            data-testid={isReject ? "offer-reject-confirm" : "offer-approve-confirm"}>
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t(isReject ? "offers.rejectAction" : "offers.approveAction")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function PickProviderDialog({ open, onOpenChange, providers, onPick, t, isRTL }) {
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    useEffect(() => { if (open) { setQ(""); setSelected(null); } }, [open]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return term ? providers.filter((p) => (p.corporate_name || "").toLowerCase().includes(term)) : providers;
    }, [providers, q]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-2xl max-h-[80vh] overflow-hidden flex flex-col"
                data-testid="pick-provider-dialog" dir={isRTL ? "rtl" : "ltr"}>
                <DialogHeader>
                    <DialogTitle>{t("offers.pickProviderTitle")}</DialogTitle>
                    <DialogDescription>{t("offers.pickProviderHint")}</DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder={t("admin.providers.searchPlaceholder")}
                        className="ps-10" autoFocus data-testid="pick-provider-search" />
                </div>
                <div className="flex-1 overflow-y-auto space-y-1 mt-3">
                    {filtered.length === 0 && <p className="text-sm text-muted-foreground p-4 text-center">{t("admin.providers.empty")}</p>}
                    {filtered.map((p) => (
                        <button key={p.id} type="button" onClick={() => setSelected(p)}
                            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-start transition-colors ${selected?.id === p.id ? "bg-primary/10 ring-2 ring-primary/40" : "hover:bg-muted/40"}`}
                            data-testid={`pick-provider-row-${p.id}`}>
                            <div className="icon-tile h-9 w-9 shrink-0 overflow-hidden">
                                {p.logo_url ? <img src={p.logo_url} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate">{p.corporate_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{t(`provider.types.${p.type}`)}</p>
                            </div>
                            {selected?.id === p.id && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
                        </button>
                    ))}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
                    <Button type="button" disabled={!selected} onClick={() => { onPick(selected); onOpenChange(false); }}
                        data-testid="pick-provider-confirm">{t("common.continue")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ViewOfferDialog({ open, onOpenChange, offer, onEdit, t, isRTL, lang }) {
    if (!offer) return null;
    const title  = lang === "ar" ? (offer.title_ar  || offer.title_en)  : (offer.title_en  || offer.title_ar);
    const desc   = lang === "ar" ? (offer.description_ar || offer.description_en) : (offer.description_en || offer.description_ar);
    const terms  = lang === "ar" ? (offer.terms_ar  || offer.terms_en)  : (offer.terms_en  || offer.terms_ar);

    const fmt = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { day: "numeric", month: "short", year: "numeric" }); }
        catch { return iso; }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl rounded-2xl overflow-hidden p-0"
                dir={isRTL ? "rtl" : "ltr"}
                data-testid="offer-view-dialog"
            >
                {/* ── Image banner ── */}
                {offer.image_url && (
                    <div className="h-40 w-full overflow-hidden bg-muted">
                        <img src={offer.image_url} alt={title} className="h-full w-full object-cover" />
                    </div>
                )}

                <div className="p-6 space-y-5">
                    {/* ── Title + status ── */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1 min-w-0">
                            <h2 className="text-xl font-semibold leading-tight">{title}</h2>
                            {offer.title_ar && offer.title_en && offer.title_ar !== offer.title_en && (
                                <p className="text-sm text-muted-foreground" dir="rtl">{lang === "ar" ? offer.title_en : offer.title_ar}</p>
                            )}
                            <p className="text-sm text-muted-foreground">{offer.provider_name}</p>
                        </div>
                        <StatusBadge status={offer.status} t={t} />
                    </div>

                    {/* ── Rejection reason ── */}
                    {offer.rejection_reason && (
                        <div className="rounded-lg bg-destructive/5 border border-destructive/20 px-4 py-3">
                            <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1">{t("offers.fields.rejectionReason")}</p>
                            <p className="text-sm text-destructive">{offer.rejection_reason}</p>
                        </div>
                    )}

                    {/* ── Key numbers ── */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="rounded-xl bg-primary/5 border border-primary/15 px-4 py-3 flex items-center gap-3">
                            <TicketPercent className="h-5 w-5 text-primary shrink-0" />
                            <div>
                                <p className="text-xs text-muted-foreground">{lang === "ar" ? "الخصم" : "Discount"}</p>
                                <p className="text-lg font-bold text-primary leading-tight">
                                    {Math.round(offer.discount_value)}{offer.discount_type === "PERCENTAGE" ? "%" : " EGP"}
                                </p>
                                {offer.original_price && (
                                    <p className="text-xs text-muted-foreground line-through">{offer.original_price} EGP</p>
                                )}
                            </div>
                        </div>
                        <div className="rounded-xl bg-muted/60 border border-border px-4 py-3 flex items-center gap-3">
                            <CalendarDays className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-xs text-muted-foreground">{lang === "ar" ? "مدة العرض" : "Duration"}</p>
                                <p className="text-sm font-medium leading-snug">{offer.start_date}</p>
                                <p className="text-xs text-muted-foreground">{lang === "ar" ? "حتى" : "to"} {offer.end_date}</p>
                            </div>
                        </div>
                        <div className="rounded-xl bg-muted/60 border border-border px-4 py-3 flex items-center gap-3 col-span-2 sm:col-span-1">
                            <Eye className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-xs text-muted-foreground">{lang === "ar" ? "الاسترداد" : "Redemptions"}</p>
                                <p className="text-lg font-bold leading-tight">{offer.redemptions_count ?? 0}</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Coverage pills ── */}
                    <div className="flex flex-wrap gap-2">
                        {(offer.location_ids || []).length > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-muted rounded-full px-3 py-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {(offer.location_ids || []).length} {lang === "ar" ? "فرع" : "location(s)"}
                            </span>
                        )}
                        {(offer.staff_ids || []).length > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-muted rounded-full px-3 py-1">
                                <Users className="h-3.5 w-3.5" />
                                {(offer.staff_ids || []).length} {lang === "ar" ? "طاقم" : "staff member(s)"}
                            </span>
                        )}
                        {(offer.category_ids || []).length > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-muted rounded-full px-3 py-1">
                                <Tag className="h-3.5 w-3.5" />
                                {(offer.category_ids || []).length} {lang === "ar" ? "فئة" : "category/ies"}
                            </span>
                        )}
                    </div>

                    {/* ── Description ── */}
                    {desc && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                {lang === "ar" ? "الوصف" : "Description"}
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">{desc}</p>
                        </div>
                    )}

                    {/* ── Terms ── */}
                    {terms && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                {lang === "ar" ? "الشروط والأحكام" : "Terms & Conditions"}
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">{terms}</p>
                        </div>
                    )}

                    {/* ── Timestamps ── */}
                    <div className="border-t border-border/50 pt-4 grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-xs text-muted-foreground">
                        {offer.created_at && <span><span className="font-medium text-foreground/60">{lang === "ar" ? "أُنشئ:" : "Created:"}</span> {fmt(offer.created_at)}</span>}
                        {offer.submitted_at && <span><span className="font-medium text-foreground/60">{lang === "ar" ? "أُرسل:" : "Submitted:"}</span> {fmt(offer.submitted_at)}</span>}
                        {offer.decided_at && <span><span className="font-medium text-foreground/60">{lang === "ar" ? "قُرّر:" : "Decided:"}</span> {fmt(offer.decided_at)}</span>}
                    </div>
                </div>

                {/* ── Footer ── */}
                <DialogFooter className="px-6 pb-6 pt-0 gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.close")}</Button>
                    <Button onClick={() => { onOpenChange(false); onEdit(offer); }} data-testid="offer-view-edit-button">
                        <Pencil className="h-4 w-4 me-2" />
                        {t("common.edit")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AllOffersPage({ defaultStatus = "ALL" }) {
    const { t, lang, isRTL } = useI18n();

    // Raw data
    const [allItems, setAllItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [providers, setProviders] = useState([]);
    const [allL2Categories, setAllL2Categories] = useState([]);

    // Filters & sort
    const [search, setSearch] = useState("");
    const [filterValues, setFilterValues] = useState(
        defaultStatus && defaultStatus !== "ALL" ? { status: defaultStatus } : {}
    );
    const [sortValue, setSortValue] = useState("created_desc");
    const [dateRange, setDateRange] = useState({ from: "", to: "" });
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ col: "created_at", dir: "desc" });

    // Dialog states
    const [pickOpen, setPickOpen] = useState(false);
    const [viewing, setViewing] = useState(null);     // offer to view
    const [creating, setCreating] = useState(null);   // { provider, locations, staff, categories }
    const [editing, setEditing] = useState(null);     // { offer, locations, staff, categories }
    const [deciding, setDeciding] = useState({ open: false, offer: null, action: null });
    const [deleting, setDeleting] = useState(null);   // offer to delete

    // ── Load ──────────────────────────────────────────────────────────────────
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const params = {
                status: filterValues.status || undefined,
                discount_type: filterValues.discount_type || undefined,
                date_from: dateRange.from || undefined,
                date_to: dateRange.to || undefined,
                sort: sortValue || undefined,
                limit: pageSize,
                skip: (page - 1) * pageSize,
            };
            const res = await adminOffersApi.list(params);
            setAllItems(res.items ?? []);
            setTotal(res.total ?? 0);
        } catch (err) {
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message || "Failed to load offers";
            setLoadError(msg);
            toast.error(msg);
        }
    }, [filterValues, dateRange.from, dateRange.to, sortValue, pageSize, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [filterValues, dateRange.from, dateRange.to, sortValue, pageSize]);

    useEffect(() => {
        adminProvidersApi.list().then((r) => setProviders(r.items || [])).catch(() => {});
        categoriesApi.listTree(false).then((tree) => {
            const flat = []; const seen = new Set();
            (tree || []).forEach((l1) => (l1.children || []).forEach((l2) => {
                if (!seen.has(l2.id)) { seen.add(l2.id); flat.push(l2); }
            }));
            setAllL2Categories(flat);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (defaultStatus && defaultStatus !== "ALL") {
            setFilterValues((p) => ({ ...p, status: defaultStatus }));
        }
    }, [defaultStatus]);

    // Search is client-side over the current page (full-text on backend would
    // need an additional index — keep it light for now).
    const rows = useMemo(() => {
        const list = allItems || [];
        if (!search.trim()) return list;
        const q = search.toLowerCase();
        return list.filter((o) =>
            (o.title_en || "").toLowerCase().includes(q) ||
            (o.title_ar || "").toLowerCase().includes(q) ||
            (o.provider_name || "").toLowerCase().includes(q)
        );
    }, [allItems, search]);

    const cycleSort = (col) => {
        setSort((s) => s.col === col
            ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
            : { col, dir: "asc" }
        );
    };

    const titleFor = (o) => lang === "ar" ? (o.title_ar || o.title_en) : (o.title_en || o.title_ar);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const onPickProvider = async (p) => {
        try {
            const [locs, st] = await Promise.all([
                adminProvidersApi.listLocations(p.id),
                adminProvidersApi.listStaff(p.id),
            ]);
            const cats = (p.category_ids || []).length > 0
                ? allL2Categories.filter((c) => (p.category_ids || []).includes(c.id))
                : allL2Categories;
            setCreating({ provider: p, locations: (locs || []).filter((l) => l.is_active), staff: (st || []).filter((s) => s.is_active), categories: cats });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const onEdit = async (offer) => {
        try {
            const [locs, st] = await Promise.all([
                adminProvidersApi.listLocations(offer.provider_id),
                adminProvidersApi.listStaff(offer.provider_id),
            ]);
            setEditing({
                offer,
                locations: (locs || []).filter((l) => l.is_active),
                staff: (st || []).filter((s) => s.is_active),
                categories: allL2Categories,
            });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const saveCreate = ({ payload }) => adminOffersApi.createForProvider(creating.provider.id, payload);
    const saveEdit   = ({ id, payload }) => adminOffersApi.update(id, payload);

    const onCreated = (saved) => {
        const enriched = { ...saved, provider_name: creating.provider.corporate_name };
        setAllItems((prev) => [enriched, ...(prev || [])]);
        setCreating(null);
    };

    const onEdited = (saved) => {
        setAllItems((prev) => prev?.map((o) => o.id === saved.id ? { ...saved } : o));
        setEditing(null);
    };

    const onDecided = (updated) => {
        setAllItems((prev) => prev?.map((o) => o.id === updated.id ? { ...o, ...updated } : o));
    };

    const onDelete = async () => {
        if (!deleting) return;
        try {
            await adminOffersApi.remove(deleting.id);
            toast.success(t("offers.toasts.deleted"));
            setAllItems((prev) => prev?.filter((o) => o.id !== deleting.id));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setDeleting(null); }
    };

    // ── Counts for status pills ───────────────────────────────────────────────
    const counts = useMemo(() => {
        const c = { ALL: 0, PENDING: 0, APPROVED: 0, REJECTED: 0, DRAFT: 0 };
        (allItems || []).forEach((o) => { c.ALL++; c[o.status] = (c[o.status] || 0) + 1; });
        return c;
    }, [allItems]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="max-w-7xl space-y-6 fade-up" data-testid="page-admin-offers">

            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">{t("offers.adminAllTitle")}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{t("offers.adminSubtitle")}</p>
                </div>
                <Button onClick={() => setPickOpen(true)} data-testid="all-offers-add-button">
                    <Plus className="h-4 w-4 me-1" />
                    {t("offers.add")}
                </Button>
            </header>

            {/* Filters bar */}
            <TableToolbar
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder={lang === "ar" ? "بحث باسم العرض أو المزود..." : "Search by offer or provider name..."}
                filters={[
                    {
                        key: "status",
                        label: t("admin.providers.cols.kyc"),
                        options: STATUSES.map((s) => ({ value: s, label: t(`offers.status.${s}`) })),
                    },
                    {
                        key: "discount_type",
                        label: lang === "ar" ? "نوع الخصم" : "Discount type",
                        options: [
                            { value: "PERCENTAGE", label: lang === "ar" ? "نسبة" : "Percentage" },
                            { value: "FIXED_PACKAGE", label: lang === "ar" ? "باقة ثابتة" : "Fixed package" },
                        ],
                    },
                ]}
                filterValues={filterValues}
                onFilterChange={(k, v) => setFilterValues((p) => ({ ...p, [k]: v }))}
                onClearFilters={() => setFilterValues({})}
                sortOptions={[
                    { value: "status_created", label: t("table.sort.statusCreated") },
                    { value: "created_desc", label: t("table.sort.createdDesc") },
                    { value: "created_asc", label: t("table.sort.createdAsc") },
                    { value: "discount_desc", label: t("table.sort.discountDesc") },
                    { value: "discount_asc", label: t("table.sort.discountAsc") },
                    { value: "end_date_asc", label: t("table.sort.endDateAsc") },
                ]}
                sortValue={sortValue}
                onSortChange={setSortValue}
                showDates
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {/* Table */}
            {allItems === null && !loadError ? (
                <div className="surface-card p-12 flex items-center justify-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">{lang === "ar" ? "جارٍ التحميل..." : "Loading offers..."}</span>
                </div>
            ) : loadError ? (
                <div className="surface-card p-10 text-center space-y-3">
                    <p className="text-sm text-destructive font-medium">{loadError}</p>
                    <Button variant="outline" size="sm" onClick={load}>{lang === "ar" ? "إعادة المحاولة" : "Retry"}</Button>
                </div>
            ) : rows.length === 0 ? (
                <div className="surface-card p-10 text-center" data-testid="offers-queue-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("offers.queueEmpty")}</p>
                    {filterValues.status && (
                        <p className="text-xs text-muted-foreground mt-1">
                            {lang === "ar" ? "جرّب تغيير فلتر الحالة إلى «الكل»" : 'Try changing the status filter to "All"'}
                        </p>
                    )}
                    {allItems?.length > 0 && rows.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                            {lang === "ar" ? `يوجد ${allItems.length} عروض إجمالاً، لكنها مصفّاة` : `${allItems.length} total offers exist but are filtered out`}
                        </p>
                    )}
                </div>
            ) : (
                <div className="surface-card overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                <TableHead>
                                    <button onClick={() => cycleSort("title_en")} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                                        {lang === "ar" ? "العرض" : "Offer"}
                                        <SortIcon col="title_en" sort={sort} />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button onClick={() => cycleSort("provider_name")} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                                        {lang === "ar" ? "المزود" : "Provider"}
                                        <SortIcon col="provider_name" sort={sort} />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button onClick={() => cycleSort("discount_value")} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                                        {lang === "ar" ? "الخصم" : "Discount"}
                                        <SortIcon col="discount_value" sort={sort} />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button onClick={() => cycleSort("end_date")} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                                        {lang === "ar" ? "التواريخ" : "Dates"}
                                        <SortIcon col="end_date" sort={sort} />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button onClick={() => cycleSort("status")} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                                        {lang === "ar" ? "الحالة" : "Status"}
                                        <SortIcon col="status" sort={sort} />
                                    </button>
                                </TableHead>
                                <TableHead className="w-12" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((offer) => (
                                <TableRow key={offer.id} className="group" data-testid={`offer-row-${offer.id}`}>

                                    {/* Title */}
                                    <TableCell className="max-w-[220px]">
                                        <button
                                            type="button"
                                            onClick={() => setViewing(offer)}
                                            className="text-start group/title w-full"
                                            data-testid={`offer-view-title-${offer.id}`}
                                        >
                                            <p className="font-medium truncate text-sm group-hover/title:text-primary group-hover/title:underline underline-offset-2 transition-colors">
                                                {titleFor(offer)}
                                            </p>
                                            {offer.rejection_reason && (
                                                <p className="text-xs text-destructive truncate mt-0.5">{offer.rejection_reason}</p>
                                            )}
                                        </button>
                                    </TableCell>

                                    {/* Provider */}
                                    <TableCell className="text-sm text-muted-foreground max-w-[150px]">
                                        <span className="truncate block">{offer.provider_name}</span>
                                    </TableCell>

                                    {/* Discount */}
                                    <TableCell>
                                        <span className="font-bold text-primary">
                                            {Math.round(offer.discount_value)}
                                            {offer.discount_type === "PERCENTAGE" ? "%" : " EGP"}
                                        </span>
                                        {offer.original_price && (
                                            <span className="block text-xs text-muted-foreground line-through">
                                                {offer.original_price} EGP
                                            </span>
                                        )}
                                    </TableCell>

                                    {/* Dates */}
                                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                        <span>{offer.start_date}</span>
                                        <span className="mx-1 opacity-50">→</span>
                                        <span>{offer.end_date}</span>
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        <StatusBadge status={offer.status} t={t} />
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                    data-testid={`offer-actions-${offer.id}`}>
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                                <DropdownMenuItem onClick={() => setViewing(offer)} data-testid={`offer-view-${offer.id}`}>
                                                    <Eye className="h-4 w-4 me-2" />
                                                    {t("common.view")}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => onEdit(offer)} data-testid={`offer-edit-${offer.id}`}>
                                                    <Pencil className="h-4 w-4 me-2" />
                                                    {t("common.edit")}
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                {offer.status !== "APPROVED" && (
                                                    <DropdownMenuItem
                                                        onClick={() => setDeciding({ open: true, offer, action: "APPROVED" })}
                                                        className="text-success focus:text-success"
                                                        data-testid={`offer-approve-${offer.id}`}>
                                                        <ShieldCheck className="h-4 w-4 me-2" />
                                                        {t("offers.approveAction")}
                                                    </DropdownMenuItem>
                                                )}
                                                {offer.status !== "REJECTED" && (
                                                    <DropdownMenuItem
                                                        onClick={() => setDeciding({ open: true, offer, action: "REJECTED" })}
                                                        className="text-destructive focus:text-destructive"
                                                        data-testid={`offer-reject-${offer.id}`}>
                                                        <ShieldX className="h-4 w-4 me-2" />
                                                        {t("offers.rejectAction")}
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={() => setDeleting(offer)}
                                                    className="text-destructive focus:text-destructive"
                                                    data-testid={`offer-delete-${offer.id}`}>
                                                    <Trash2 className="h-4 w-4 me-2" />
                                                    {t("common.delete")}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                </div>
            )}

            {/* ── Dialogs ────────────────────────────────────────────────── */}
            <ViewOfferDialog
                open={!!viewing}
                onOpenChange={(o) => !o && setViewing(null)}
                offer={viewing}
                onEdit={onEdit}
                t={t} isRTL={isRTL} lang={lang}
            />

            <DecisionDialog
                open={deciding.open}
                onOpenChange={(o) => setDeciding((d) => ({ ...d, open: o }))}
                offer={deciding.offer}
                decision={deciding.action}
                onDecided={onDecided}
                t={t} isRTL={isRTL}
            />

            <PickProviderDialog
                open={pickOpen}
                onOpenChange={setPickOpen}
                providers={providers}
                onPick={onPickProvider}
                t={t} isRTL={isRTL}
            />

            {/* Create offer */}
            <OfferFormDialog
                open={!!creating}
                onOpenChange={(o) => !o && setCreating(null)}
                initial={null}
                save={saveCreate}
                onSaved={onCreated}
                locations={creating?.locations || []}
                staff={creating?.staff || []}
                categories={creating?.categories || []}
            />

            {/* Edit offer */}
            <OfferFormDialog
                open={!!editing}
                onOpenChange={(o) => !o && setEditing(null)}
                initial={editing?.offer || null}
                save={saveEdit}
                onSaved={onEdited}
                locations={editing?.locations || []}
                staff={editing?.staff || []}
                categories={editing?.categories || []}
            />

            {/* Delete confirm */}
            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent data-testid="offer-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("offers.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("offers.confirmDeleteBody", { name: deleting ? titleFor(deleting) : "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="offer-delete-confirm-button">
                            {t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
